import { useEffect, useRef, useState } from 'react'

/** A loading indicator becomes visible only after this long… */
export const LOADING_DELAY_MS = 180
/** …and, once visible, stays at least this long. */
export const LOADING_MIN_VISIBLE_MS = 500

/**
 * How long to keep reporting "loading" after the work finished.
 *
 * Pure, so the timing is testable without a DOM. The indicator itself only
 * becomes visible `delayMs` after the work started (`.q-loading` fades in
 * with that delay), so:
 *   - work shorter than the delay was never seen: hold nothing;
 *   - otherwise keep it until it has been visible for `minMs`.
 */
export function loadingHoldMs(startedAt: number | null, endedAt: number, delayMs: number, minMs: number): number {
  if (startedAt === null) return 0
  const ran = endedAt - startedAt
  if (ran < delayMs) return 0
  const visibleFor = ran - delayMs
  return Math.max(0, minMs - visibleFor)
}

/**
 * `true` while `active`, plus a short tail if the indicator was actually seen.
 *
 * Replaces useMinimumDuration, which held *every* loading state for 1.5 s —
 * a list that loaded from the device in 40 ms still showed a spinner for a
 * second and a half, and the whole app felt slow (audit M6). Now:
 *
 *   loads in 40 ms   → nothing visible, content right away
 *   loads in 900 ms  → spinner from 180 ms to 900 ms (≥ 500 ms visible)
 *
 * Callers keep rendering their loading branch while this is `true` (so an
 * empty state never flashes before the data), and use `<LoadingState>` /
 * `.q-loading`, which is transparent for the first `delayMs`.
 *
 * ```tsx
 * const showLoading = useLoadingIndicator(isLoading)
 * if (showLoading) return <LoadingState label={t('decks.loading')} />
 * ```
 */
export function useLoadingIndicator(
  active: boolean,
  { delayMs = LOADING_DELAY_MS, minMs = LOADING_MIN_VISIBLE_MS }: { delayMs?: number; minMs?: number } = {}
): boolean {
  const [inTail, setInTail] = useState(false)
  const startedAt = useRef<number | null>(null)

  useEffect(() => {
    if (active) {
      if (startedAt.current === null) startedAt.current = Date.now()
      return
    }
    const hold = loadingHoldMs(startedAt.current, Date.now(), delayMs, minMs)
    startedAt.current = null
    if (hold === 0) return
    setInTail(true)
    const timer = setTimeout(() => setInTail(false), hold)
    return () => clearTimeout(timer)
  }, [active, delayMs, minMs])

  return active || inTail
}
